import { offerWindow } from 'helpers/offerWindow';
import { useModernDesktop } from 'components/Layout/ModernDesktopShell';
import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import styles from '../styles/pages/MatchOfferList.module.css';
import { MatchMobile } from 'components/mobile/MatchMobile/MatchMobile';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { capitalizeFirstWord } from 'helpers/stringHelper';
import { PrematchCatalogContext } from 'context/prematchCatalogContext';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MatchOffer } from 'types/MatchOffer';
import useAsyncEffect from 'use-async-effect';
import { getEventsByLeague } from 'services/api/sportBetsService';
import { Loader } from 'components/Loader/Loader';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { FULL_TIME_BETGROUP_NAME } from 'config/constants';
import { useTranslation } from 'react-i18next';
import { translateBetMarketNameForSelection } from 'helpers/betI18n';
import { getAllBets } from 'helpers/matchHelper';
import { Bet } from 'types/Bet';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { usePlayerLayout } from 'lib/playerLayout';
import classNames from 'classnames';
import { ModernSportNav } from 'components/mobile/ModernSportNav/ModernSportNav';
import { MatchInsightsModal } from 'components/MatchInsights/MatchInsightsModal';
import { VipRoundButton } from 'components/mobile/VipChrome/VipChrome';
import { VipIcon } from 'components/mobile/VipChrome/VipIcons';
import { dayMonth, weekdayName } from 'helpers/weekday';
import { MarketPicker } from 'components/mobile/LiveBetting/LiveBetting';
import { marketOrderIndex, UNLISTED_MARKET_ORDER } from 'helpers/marketCatalog';

/** VIP: the list by day, each match in kick-off order under its day. */
const matchDays = (matches: MatchOffer[] | null, language: string) => {
  const days: { key: string; label: string; matches: MatchOffer[] }[] = [];
  matches?.forEach((m) => {
    const date = new Date(m.time * 1000);
    const key = date.toDateString();
    const last = days[days.length - 1];
    if (last?.key === key) last.matches.push(m);
    else
      days.push({
        key,
        label: `${weekdayName(date, language)}, ${dayMonth(date)}`,
        matches: [m],
      });
  });
  return days;
};

export const MatchOfferList = () => {
  const { lastPrematchPush } = useContext(PrematchCatalogContext);
  const [matches, setMatches] = useState<MatchOffer[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // The league's table is open, read from this match.
  const [tableFi, setTableFi] = useState<string | number | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<string>(
    FULL_TIME_BETGROUP_NAME
  );
  const { isMobile } = useBreakpoint();
  const modernDesktop = useModernDesktop();
  const layout = usePlayerLayout();
  const modern = layout === 'modern';
  const vip = layout === 'vip';
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const urlParts = useLocation().pathname.split('/');

  const leagueId = urlParts.pop();
  const leagueName = decodeURI(urlParts.pop()!);
  const sportId = urlParts.pop();
  const sportName = decodeURI(urlParts.pop()!);
  const [searchParams] = useSearchParams();
  const time = searchParams.get('time');
  // ?time= is an offer window key (helpers/offerWindow).
  const offer = useMemo(() => offerWindow(time), [time]);
  const dateTime = offer.to;
  const sportIdNumber = useMemo(() => Number.parseInt(sportId!, 10), [sportId]);
  const prematchRefreshKey = useMemo(() => {
    if (!lastPrematchPush?.sportIds.includes(sportIdNumber)) {
      return 0;
    }
    return lastPrematchPush.version;
  }, [lastPrematchPush, sportIdNumber]);

  let allBets: Bet[] = [];
  if (matches && matches.length > 0) {
    // The market list comes from the match with the most markets; a copy is
    // sorted, so the list itself stays in kick-off order.
    allBets = getAllBets(
      [...matches].sort((x, y) => {
        const betsXCount = getAllBets(x).length;
        const betsYCount = getAllBets(y).length;
        return betsYCount - betsXCount;
      })[0],
      true
    );
  }

  // Another league may not have the market picked here.
  useEffect(() => setSelectedFilter(FULL_TIME_BETGROUP_NAME), [leagueId]);

  // A newly opened list shows 1 / X / 2 from a light answer first, and the
  // full markets replace it right behind, usually before a market chip is
  // tapped. New prices for a list already shown come in without the loader.
  const shownListRef = useRef('');
  useAsyncEffect(
    async (isMounted) => {
      const lid = Number.parseInt(leagueId!);
      const sid = Number.parseInt(sportId!);
      const list = `${leagueId}|${sportId}|${dateTime?.getTime()}|${offer.from?.getTime()}`;
      if (list !== shownListRef.current) {
        setIsLoading(true);
        const first = await getEventsByLeague(
          lid,
          sid,
          dateTime,
          offer.from,
          true
        );
        if (!isMounted()) return;
        shownListRef.current = list;
        setMatches(first);
        setIsLoading(false);
      }
      const full = await getEventsByLeague(lid, sid, dateTime, offer.from);
      if (isMounted() && full) setMatches(full);
    },
    [leagueId, sportId, dateTime, offer.from, prematchRefreshKey]
  );

  if (!isMobile && !modernDesktop) {
    return <Navigate to="/not-found" />;
  }

  // One small list of markets, the same one as on the live page.
  // The main and the alternative lines of a bet share one name (catalogue),
  // which read as the same market twice in the list: the second one is called
  // "... – more lines". Two feed markets of one catalogue row are one bet and
  // listed once (owner, 2026-11-03).
  const pickerLabels = new Map<string, string>();
  const usedLabels = new Set<string>();
  const usedRows = new Set<number>();
  allBets.forEach((b) => {
    const row = marketOrderIndex(b.name);
    if (row !== UNLISTED_MARKET_ORDER && usedRows.has(row)) return;
    const base = translateBetMarketNameForSelection(b.name, allBets, t);
    const label = usedLabels.has(base)
      ? `${base} – ${t('market_more_lines')}`
      : base;
    if (usedLabels.has(label)) return;
    usedRows.add(row);
    usedLabels.add(label);
    pickerLabels.set(b.name, label);
  });
  const pickerMarkets = Array.from(pickerLabels.keys()).map((key) => ({ key }));
  const marketPicker = pickerMarkets.length > 1 && (
    <MarketPicker
      markets={pickerMarkets}
      current={{ key: selectedFilter }}
      label={(m) =>
        pickerLabels.get(m.key) ??
        translateBetMarketNameForSelection(m.key, allBets, t)
      }
      onPick={(m) => setSelectedFilter(m.key)}
    />
  );

  return (
    <MobileLayout
      hideBack={modern}
      pageTitle={
        modern ? 'sports_betting_label' : capitalizeFirstWord(leagueName!)
      }
      topContent={
        modern ? (
          <ModernSportNav
            sportId={sportIdNumber}
            leagueId={Number.parseInt(leagueId!, 10)}
          />
        ) : undefined
      }
      headerTrailing={
        vip ? (
          <VipRoundButton
            label={t('search_label')}
            onClick={() => navigate('/search')}
          >
            <VipIcon name="search" />
          </VipRoundButton>
        ) : undefined
      }
    >
      {vip ? (
        <div className={styles.vipOffer}>
          {/* One line on top, as on the live page: the match count and the
              league table on the left, the market to show on the right in a
              small list instead of a long row of buttons (owner, 2026-11-02). */}
          <div className={styles.vipTopRow}>
            <span>
              {t('vip_matches', { count: matches?.length ?? 0 })}
              {!!matches?.length && (
                <button
                  type="button"
                  className={styles.vipTable}
                  aria-label={t('match_tab_table')}
                  onClick={() => setTableFi(matches[0].fi)}
                >
                  <VipIcon name="chart" size={13} />
                </button>
              )}
            </span>
            {marketPicker}
          </div>
          {isLoading && <Loader />}
          {!isLoading &&
            matchDays(matches, i18n.language).map((day) => (
              <section key={day.key} className={styles.vipDay}>
                <div className={styles.vipDayHead}>{day.label}</div>
                {day.matches.map((m) => (
                  <MatchMobile key={m.fi} match={m} betName={selectedFilter} />
                ))}
              </section>
            ))}
        </div>
      ) : modern ? (
        <div className={styles.modernOffer}>
          <div className={styles.modernLeague}>
            <SportIcon
              className={styles.sportIcon}
              sportId={sportId}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = getDefaultSportIconSrc();
              }}
              alt={sportName}
            />
            <span>{leagueName}</span>
            {marketPicker}
          </div>
          <div className={styles.matches}>
            {isLoading && <Loader />}
            {!isLoading &&
              matches?.map((m) => (
                <MatchMobile key={m.fi} match={m} betName={selectedFilter} />
              ))}
          </div>
        </div>
      ) : (
        <div className={styles.wrapper}>
          {/* The market in a small list on the league's bar, as on the live
              page, instead of two rows and a separate page (owner, 2026-11-03). */}
          <div
            className={classNames(
              styles.leagueInfoWrapper,
              styles.leagueInfoWithPicker
            )}
          >
            <SportIcon
              className={styles.sportIcon}
              sportId={sportId}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = getDefaultSportIconSrc();
              }}
              alt={sportName}
            />
            <span>
              {capitalizeFirstWord(sportName!)} - {leagueName}
            </span>
            {marketPicker}
            {/* The league's table, read from one of its matches. */}
            <button
              type="button"
              className={styles.statsIcon}
              aria-label={t('match_tab_table')}
              disabled={!matches?.length}
              onClick={() => matches?.length && setTableFi(matches[0].fi)}
            />
          </div>
          <div className={styles.matches}>
            {isLoading && <Loader />}
            {!isLoading &&
              matches &&
              matches.length > 0 &&
              matches.map((m) => (
                <MatchMobile key={m.fi} match={m} betName={selectedFilter} />
              ))}
          </div>
        </div>
      )}
      {tableFi != null && (
        <MatchInsightsModal
          fi={tableFi}
          initialTab="table"
          onClose={() => setTableFi(null)}
        />
      )}
    </MobileLayout>
  );
};
